import React from 'react'
import { toast } from 'react-toastify'

import {
    TableRow,
    TableCell,
    CircularProgress,
    Typography,
    useTheme,
    Table,
    TableHead,
    TableBody,
    TablePagination,
    TableContainer,
    TextField,
    InputAdornment,
    Button,
    Switch,
    FormControlLabel,
    Box,
} from '@material-ui/core'
import { KeyboardArrowUp, KeyboardArrowDown, Search } from '@material-ui/icons'

import { usePagination } from '../../../hooks/usePagination'
import { PaymentSchedule, BillingGroup } from '../../../models/Billing/types'
import { BillingGroupModal } from './BillingGroupModal'
import { CreateBillingGroupModal } from './CreateBillingGroupModal'
import { CreateInvoiceModal } from './CreateInvoiceModal'
import { CompanyWithInvoiceCode } from '../Property/types'
import { useProperties } from '../hooks'
import { useBillingGroups } from '../../../hooks/useBillingGroups'
import {
    CreateBillingGroupRequest,
    CreateInvoiceRequest,
} from '../../../hooks/useBillingGroups'

export const BillingGroupHome = () => {
    const {
        billingGroups,
        isLoading,
        isError,
        error,
        refetch,
        updateBillingGroup,
        removeCompanyFromBillingGroup,
        addCompanyToBillingGroup,
        createBillingGroup,
        createInvoice,
    } = useBillingGroups()

    const { propertyList } = useProperties()

    const theme = useTheme()

    const rows = billingGroups || []
    const [searchTerm, setSearchTerm] = React.useState('')
    const [showActiveOnly, setShowActiveOnly] = React.useState(true)
    const [
        selectedBillingGroup,
        setSelectedBillingGroup,
    ] = React.useState<BillingGroup | null>(null)
    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false)
    const [
        isCreateInvoiceModalOpen,
        setIsCreateInvoiceModalOpen,
    ] = React.useState(false)
    const [
        selectedBillingGroupForInvoice,
        setSelectedBillingGroupForInvoice,
    ] = React.useState<BillingGroup | null>(null)

    // Update selectedBillingGroup when billingGroups changes
    React.useEffect(() => {
        if (selectedBillingGroup && billingGroups) {
            const updatedBillingGroup = billingGroups.find(
                (bg) => bg.id === selectedBillingGroup.id,
            )
            if (updatedBillingGroup) {
                setSelectedBillingGroup(updatedBillingGroup)
            }
        }
    }, [billingGroups, selectedBillingGroup])

    const [sortField, setSortField] = React.useState<
        | 'display_name'
        | 'organization_name'
        | 'payment_schedule'
        | 'invoice_email'
        | 'property_count'
        | 'last_invoice_date'
    >('display_name')
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
        'asc',
    )

    const filteredRows = React.useMemo(() => {
        let filtered = rows

        // Filter by active status - show either active OR inactive, not both
        filtered = filtered.filter((billingGroup) =>
            showActiveOnly ? billingGroup.active : !billingGroup.active,
        )

        // Filter by search term
        if (!searchTerm.trim()) return filtered

        return filtered.filter((billingGroup) => {
            const searchLower = searchTerm.toLowerCase()
            return (
                billingGroup.quickbooks_customer.display_name
                    .toLowerCase()
                    .includes(searchLower) ||
                billingGroup.organization.name
                    .toLowerCase()
                    .includes(searchLower) ||
                billingGroup.invoice_email
                    .toLowerCase()
                    .includes(searchLower) ||
                billingGroup.payment_schedule
                    .toLowerCase()
                    .includes(searchLower) ||
                billingGroup.companies.length
                    .toString()
                    .includes(searchLower) ||
                (billingGroup.last_invoice_date &&
                    new Date(billingGroup.last_invoice_date)
                        .toLocaleDateString()
                        .toLowerCase()
                        .includes(searchLower))
            )
        })
    }, [rows, searchTerm, showActiveOnly])

    const sortedRows = React.useMemo(() => {
        return [...filteredRows].sort((a, b) => {
            let aValue: string | number
            let bValue: string | number

            switch (sortField) {
                case 'display_name':
                    aValue = a.quickbooks_customer.display_name
                    bValue = b.quickbooks_customer.display_name
                    break
                case 'organization_name':
                    aValue = a.organization.name
                    bValue = b.organization.name
                    break
                case 'payment_schedule':
                    aValue = a.payment_schedule
                    bValue = b.payment_schedule
                    break
                case 'invoice_email':
                    aValue = a.invoice_email
                    bValue = b.invoice_email
                    break
                case 'property_count':
                    aValue = a.companies.length
                    bValue = b.companies.length
                    break
                case 'last_invoice_date':
                    aValue = a.last_invoice_date || ''
                    bValue = b.last_invoice_date || ''
                    break
                default:
                    return 0
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue)
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc'
                    ? aValue - bValue
                    : bValue - aValue
            }

            return 0
        })
    }, [filteredRows, sortField, sortDirection])

    const {
        pageData,
        page,
        setPage,
        pageSize,
        setPageSize,
        numPages,
        start,
        end,
    } = usePagination(sortedRows, 25)

    const handleSort = (field: typeof sortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const handleRowClick = (billingGroup: BillingGroup) => {
        setSelectedBillingGroup(billingGroup)
        setIsModalOpen(true)
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
        setSelectedBillingGroup(null)
    }

    const handleSaveBillingGroup = async (
        billingGroupId: number,
        invoiceEmail: string,
        paymentSchedule: PaymentSchedule,
        active: boolean,
    ) => {
        try {
            await updateBillingGroup({
                billing_group_id: billingGroupId,
                body: {
                    invoice_email: invoiceEmail,
                    payment_schedule: paymentSchedule,
                    active: active,
                },
            })
        } catch (error) {
            // Error is already handled in the hook with toast
            console.error('Failed to update billing group:', error)
        }
    }

    const handleRemoveCompany = async (
        billingGroupId: number,
        company: CompanyWithInvoiceCode,
    ) => {
        try {
            await removeCompanyFromBillingGroup({
                billing_group_id: billingGroupId,
                company_id: company.id,
            })
            toast.success(
                `${company.name} removed from billing group successfully`,
            )
        } catch (error) {
            // Error is already handled in the hook with toast
            console.error('Failed to remove company from billing group:', error)
        }
    }

    const handleAddCompany = async (
        billingGroupId: number,
        companyId: number,
    ) => {
        try {
            const addedProperty = propertyList?.data.find(
                (p) => p.id === companyId,
            )
            if (addedProperty) {
                await addCompanyToBillingGroup(
                    {
                        billing_group_id: billingGroupId,
                        company_id: companyId,
                    },
                    addedProperty,
                )
                toast.success(
                    `${addedProperty.name} added to billing group successfully`,
                )
            }
        } catch (error) {
            // Error is already handled in the hook with toast
            console.error('Failed to add company to billing group:', error)
        }
    }

    const handleCreateBillingGroup = async (
        data: CreateBillingGroupRequest,
    ) => {
        try {
            await createBillingGroup(data)
        } catch (error) {
            // Error is already handled in the hook with toast
            console.error('Failed to create billing group:', error)
        }
    }

    const handleCreateInvoice = async (data: CreateInvoiceRequest) => {
        try {
            await createInvoice(data)
        } catch (error) {
            // Error is already handled in the hook with toast
            console.error('Failed to create invoice:', error)
        }
    }

    const handleCreateInvoiceClick = (
        billingGroup: BillingGroup,
        e: React.MouseEvent,
    ) => {
        e.stopPropagation() // Prevent row click
        setSelectedBillingGroupForInvoice(billingGroup)
        setIsCreateInvoiceModalOpen(true)
    }

    const handleCreateInvoiceModalClose = () => {
        setIsCreateInvoiceModalOpen(false)
        setSelectedBillingGroupForInvoice(null)
    }

    const SortIcon = ({ field }: { field: typeof sortField }) => {
        if (sortField !== field) return null
        return sortDirection === 'asc' ? (
            <KeyboardArrowUp />
        ) : (
            <KeyboardArrowDown />
        )
    }

    const renderRow = (billingGroup: BillingGroup) => (
        <TableRow
            key={billingGroup.id}
            hover
            onClick={() => handleRowClick(billingGroup)}
            style={{ cursor: 'pointer' }}
        >
            <TableCell
                style={{ padding: theme.spacing(2, 1.5), fontWeight: 500 }}
            >
                {billingGroup.quickbooks_customer.display_name}
            </TableCell>
            <TableCell style={{ padding: theme.spacing(2, 1.5) }}>
                {billingGroup.organization.name}
            </TableCell>
            <TableCell
                align="center"
                style={{ padding: theme.spacing(2, 1.5) }}
            >
                <span
                    style={{
                        backgroundColor:
                            billingGroup.payment_schedule ===
                            PaymentSchedule.MONTHLY
                                ? theme.palette.primary.light
                                : theme.palette.secondary.light,
                        color:
                            billingGroup.payment_schedule ===
                            PaymentSchedule.MONTHLY
                                ? theme.palette.primary.dark
                                : theme.palette.secondary.dark,
                        padding: theme.spacing(0.5, 1.5),
                        borderRadius: theme.spacing(1.5),
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    {billingGroup.payment_schedule === PaymentSchedule.MONTHLY
                        ? 'Monthly'
                        : 'Annually'}
                </span>
            </TableCell>
            <TableCell
                style={{
                    padding: theme.spacing(2, 1.5),
                    color: theme.palette.text.secondary,
                }}
            >
                {billingGroup.invoice_email}
            </TableCell>
            <TableCell
                align="center"
                style={{
                    padding: theme.spacing(2, 1.5),
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                }}
            >
                {billingGroup.companies.length}
            </TableCell>
            <TableCell
                align="center"
                style={{
                    padding: theme.spacing(2, 1.5),
                    color: theme.palette.text.secondary,
                }}
            >
                {billingGroup.last_invoice_date
                    ? new Date(
                          billingGroup.last_invoice_date,
                      ).toLocaleDateString()
                    : 'Never'}
            </TableCell>
            <TableCell
                align="center"
                style={{
                    padding: theme.spacing(2, 1.5),
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={(e) => handleCreateInvoiceClick(billingGroup, e)}
                    style={{
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: '100px',
                    }}
                >
                    Create Invoice
                </Button>
            </TableCell>
        </TableRow>
    )

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: theme.spacing(4),
                    minHeight: '400px',
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress size={40} />
                    <Typography
                        variant="body2"
                        style={{
                            marginTop: theme.spacing(2),
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Loading billing groups...
                    </Typography>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div
                style={{
                    margin: theme.spacing(1),
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.dark,
                    border: `1px solid ${theme.palette.error.main}`,
                    borderRadius: theme.spacing(1),
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                }}
            >
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div>
                    <Typography
                        variant="subtitle1"
                        style={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        Error Loading Billing Groups
                    </Typography>
                    <Typography variant="body2">
                        {error?.message || 'An unexpected error occurred'}
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[50],
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    padding: theme.spacing(1, 2),
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <Typography
                        variant="h4"
                        style={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            margin: 0,
                        }}
                    >
                        Billing Groups
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        + Add New Billing Group
                    </Button>
                </div>
                <div style={{ marginTop: theme.spacing(1) }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search billing groups..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '300px' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showActiveOnly}
                                    onChange={(e) =>
                                        setShowActiveOnly(e.target.checked)
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 500 }}
                                >
                                    {showActiveOnly ? 'Active' : 'Inactive'}
                                </Typography>
                            }
                            style={{ margin: 0, marginLeft: theme.spacing(2) }}
                        />
                    </div>
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(0.5, 2, 1, 2),
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.spacing(1),
                        boxShadow: theme.shadows[1],
                        overflow: 'hidden',
                        minHeight: 0,
                    }}
                >
                    <TableContainer
                        style={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight: 'calc(100vh - 230px)',
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('display_name')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Name
                                            <SortIcon field="display_name" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('organization_name')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Organization
                                            <SortIcon field="organization_name" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('payment_schedule')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Payment Schedule
                                            <SortIcon field="payment_schedule" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('invoice_email')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Invoice Email
                                            <SortIcon field="invoice_email" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('property_count')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Properties
                                            <SortIcon field="property_count" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            handleSort('last_invoice_date')
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            Last Invoice Date
                                            <SortIcon field="last_invoice_date" />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        style={{
                                            padding: theme.spacing(1, 1.5),
                                            fontSize: '14px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageData.map((billingGroup) =>
                                    renderRow(billingGroup),
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <div
                        style={{
                            borderTop: `1px solid ${theme.palette.divider}`,
                            padding: theme.spacing(1),
                            backgroundColor: theme.palette.background.paper,
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <TablePagination
                            count={sortedRows.length}
                            page={page - 1}
                            onChangePage={(_, newPage: number) =>
                                setPage(newPage + 1)
                            }
                            rowsPerPage={pageSize}
                            onChangeRowsPerPage={(
                                event: React.ChangeEvent<HTMLInputElement>,
                            ) => setPageSize(parseInt(event.target.value, 10))}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} of ${count}`
                            }
                        />
                    </div>
                </div>
            </div>

            <BillingGroupModal
                open={isModalOpen}
                billingGroup={selectedBillingGroup}
                propertyList={propertyList?.data || []}
                onClose={handleModalClose}
                onSave={handleSaveBillingGroup}
                onRemoveCompany={handleRemoveCompany}
                onAddCompany={handleAddCompany}
            />

            <CreateBillingGroupModal
                open={isCreateModalOpen}
                propertyList={propertyList?.data || []}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleCreateBillingGroup}
            />

            <CreateInvoiceModal
                open={isCreateInvoiceModalOpen}
                billingGroup={selectedBillingGroupForInvoice}
                onClose={handleCreateInvoiceModalClose}
                onCreate={handleCreateInvoice}
            />
        </div>
    )
}
